import { Badge, Flex, Text } from "@mantine/core";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  deleteTerminal,
  getLocateList,
  getTerminalList,
  getTerminalTelemetryLatest,
} from "../../api/terminalApi";
import TableEditButton from "../common/Buttons/TableEditButton";
import TableDeleteButton from "../common/Buttons/TableDeleteButton";
import CustomSelect from "../common/Inputs/CustomSelect";
import DataTable, { Column } from "../common/Table/DataTable";
import { useTableQuery } from "../../hooks/useTableQuery";
import { useDeleteWithConfirm } from "../../hooks/useDeleteWithConfirm";

// Terminal 60 saniyede bir telemetri gönderiyor. 5 dakika sessizlik "bayat",
// 20 dakika "kritik" sayılır — birkaç örneğin kaçması normal (WiFi flap'i,
// OTA kontrolü), sürekli sessizlik değil.
const STALE_AFTER_MS = 5 * 60 * 1000;
const CRITICAL_AFTER_MS = 20 * 60 * 1000;

const statusOf = (at?: string) => {
  if (!at) return { label: "Veri yok", color: "gray" as const };
  const age = Date.now() - new Date(at).getTime();
  if (age > CRITICAL_AFTER_MS) return { label: "Kritik", color: "red" as const };
  if (age > STALE_AFTER_MS) return { label: "Bayat", color: "orange" as const };
  return { label: "Çevrimiçi", color: "teal" as const };
};

const TabletsTable = () => {
  const navigate = useNavigate();
  const [selectData, setSelectData] = useState<any[]>([]);
  const [telemetry, setTelemetry] = useState<Record<string, any>>({});

  const table = useTableQuery({ fetcher: getTerminalList });

  useEffect(() => {
    const getLocates = async () => {
      try {
        const { data }: any = await getLocateList();
        setSelectData(data || []);
      } catch (error) {
        console.error(error);
      }
    };
    getLocates();
  }, []);

  // Son telemetri örnekleri terminalId'ye göre indekslenir. Başarısız olursa
  // sessizce geçilir: teşhis bilgisi, tablonun çalışmasını engellememeli.
  useEffect(() => {
    const getTelemetry = async () => {
      try {
        const res: any = await getTerminalTelemetryLatest();
        if (!res?.data?.success) return;
        const byId: Record<string, any> = {};
        (res.data.data || []).forEach((t: any) => {
          byId[String(t.terminalId)] = t;
        });
        setTelemetry(byId);
      } catch (error) {
        console.error("Telemetri durumu alınamadı", error);
      }
    };
    getTelemetry();
  }, []);

  const { askDelete, modal } = useDeleteWithConfirm({
    deleteFn: deleteTerminal,
    onDeleted: table.refetch,
    title: "Tablet Sil",
    text: "Bu tableti silmek istediğinizden emin misiniz?",
  });

  const columns: Column[] = [
    {
      key: "name",
      header: `Tablet Adı ${table.sort === "asc" ? "↑" : "↓"}`,
      width: "26%",
      onHeaderClick: () => table.setSort(table.sort === "asc" ? "desc" : "asc"),
      render: (row) => (
        <Text fw={400} size="md" style={{ color: "#111827" }}>
          {row.name || "—"}
        </Text>
      ),
    },
    {
      key: "code",
      header: "Terminal Kodu",
      width: "20%",
      render: (row) => (
        <Text size="md" fw={400} style={{ color: "#111827" }}>
          {row.code || "—"}
        </Text>
      ),
    },
    {
      key: "location",
      header: "Tablet Konumu",
      width: "22%",
      render: (row) => (
        <Text size="md" fw={400} style={{ color: "#111827" }}>
          {row.location || "Belirtilmemiş"}
        </Text>
      ),
    },
    {
      key: "status",
      header: "Durum",
      width: "14%",
      render: (row) => {
        const s = statusOf(telemetry[String(row._id)]?.at);
        return (
          <Badge color={s.color} variant="light" size="md">
            {s.label}
          </Badge>
        );
      },
    },
    {
      key: "actions",
      header: "İşlemler",
      width: "18%",
      headerAlign: "right",
      render: (row) => (
        <Flex justify="flex-end" gap={6} onClick={(e) => e.stopPropagation()}>
          <TableEditButton
            onClick={() =>
              navigate("/tablets/edit", { state: { tablet: row } })
            }
          />
          <TableDeleteButton onClick={() => askDelete(row)} />
        </Flex>
      ),
    },
  ];

  return (
    <>
      <DataTable
        columns={columns}
        rows={table.rows}
        loading={table.loading}
        emptyText="Tablet bulunamadı."
        onRowClick={(row) => navigate(`/tablets/${row._id}`)}
        searchValue={table.search}
        onSearchChange={table.setSearch}
        searchPlaceholder="Tablet Adı ara..."
        toolbar={
          <CustomSelect
            placeholder="Tablet Konumu"
            data={selectData}
            clearable
            onChange={(value: any) =>
              table.setFilter("locate", value ?? undefined)
            }
          />
        }
        page={table.page}
        totalPages={table.totalPages}
        onPageChange={table.setPage}
        limit={table.limit}
        onLimitChange={table.setLimit}
      />
      {modal}
    </>
  );
};

export default TabletsTable;
